// ./src/components/features/EmbeddingEventTemplates/EmbeddingEventTemplatesTable/EmbeddingEventTemplatesTable.tsx
import React, { useMemo } from "react";
import { Text, Badge } from "@mantine/core";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  StatusBadge,
} from "@/components/generic";

import { EmbeddingEventTemplate } from "@/utils/api/EmbeddingEventTemplateService";

export interface EmbeddingEventTemplatesTableProps {
  /** Array of templates to display */
  templates: EmbeddingEventTemplate[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditTemplate?: (template: EmbeddingEventTemplate) => void;

  /** Callback when duplicate action is clicked */
  onDuplicateTemplate?: (template: EmbeddingEventTemplate) => void;

  /** Callback when remove action is clicked */
  onRemoveTemplate?: (template: EmbeddingEventTemplate) => void;

  /** Callback when view/preview action is clicked */
  onViewTemplate?: (template: EmbeddingEventTemplate) => void;

  /** ID of template currently being removed */
  removingTemplateId?: string | null;

  /** ID of template that was just added or edited */
  editedTemplateId?: string | null;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const EmbeddingEventTemplatesTable: React.FC<
  EmbeddingEventTemplatesTableProps
> = ({
  templates,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditTemplate,
  onDuplicateTemplate,
  onRemoveTemplate,
  onViewTemplate,
  removingTemplateId,
  editedTemplateId,
  size = "md",
}) => {
  // Define table columns
  const columns: AppTableColumn<EmbeddingEventTemplate>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Template Name & Description",
        sortable: true,
        flex: true,
        minWidth: 250,
        render: (value, record) => (
          <div>
            <Text fw={500}>{value || "Untitled Template"}</Text>
            {record.description && (
              <Text size="xs" c="dimmed" lineClamp={2}>
                {record.description}
              </Text>
            )}
          </div>
        ),
      },
      {
        key: "layoutType",
        label: "Layout Type",
        sortable: true,
        width: 150,
        minWidth: 150,
        align: "left",
        render: (value) => {
          if (!value) return <Text c="dimmed">—</Text>;
          return (
            <Badge variant="light" size="sm">
              {value}
            </Badge>
          );
        },
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 140,
        minWidth: 140,
        align: "left",
        render: (value) => {
          return <StatusBadge status={value} />;
        },
      },
      {
        key: "actions",
        label: "",
        width: 230,
        minWidth: 230,
        align: "left",
        render: (_, record: any) => (
          <RecordActions
            context={record}
            onViewClick={onViewTemplate}
            onEditClick={
              record.rights?.includes("edit") ? onEditTemplate : undefined
            }
            onRemoveClick={
              record.rights?.includes("remove") ? onRemoveTemplate : undefined
            }
            onDuplicateClick={
              record.rights?.includes("duplicate")
                ? onDuplicateTemplate
                : undefined
            }
            removeInProgress={removingTemplateId === record.id}
          />
        ),
      },
    ],
    [
      onEditTemplate,
      onDuplicateTemplate,
      onRemoveTemplate,
      onViewTemplate,
      removingTemplateId,
    ]
  );

  return (
    <AppTable
      data={templates}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(template) => template?.id || ""}
      highlightRow={(template) => template?.id === editedTemplateId}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
